// Promo terms text shown in the "View details" modal beneath the promo
// code input.
//
// `DEFAULT_PROMO_TERMS` is the FITTINGUS terms-and-conditions copy that's
// always available — pre-populated on first render and restored whenever
// the user clears the promo field or enters a code that doesn't validate.
//
// `MOCK_VALIDATED_PROMO_TERMS` is a placeholder used until Dhruv's
// /PromoValidate response includes the upstream `termsandConditions`
// field (the SF shape already declares it; the payload doesn't populate
// it yet). When the API returns terms, plumb the response value through
// VALIDATE_PROMO_SUCCESS.payload.terms instead of this constant.

// Ultimate fallback code used when neither /fittings.activePromoCode nor the
// country-config initialPromoCode has resolved a master. In prod every store
// returns activePromoCode via /fittings, so this fires only in the sub-second
// window before the store fetch completes (or if the fetch fails entirely).
// Client's preferred fallback is CUSTOMFIT (the US master); it's a real code
// in SF so a race-condition Schedule POST still applies a valid discount
// instead of a defunct one.
export const DEFAULT_PROMO_CODE = 'CUSTOMFIT';

export const DEFAULT_PROMO_TERMS = `Use promo code FITTINGUS to get a free Club Champion club fitting when you make a custom club purchase. Promo excludes golf ball fittings. Fittings must be booked and completed by 6/30/26. Promo code provided must be used at the time of booking. Club purchase must be made the same day as the club fitting for discount to apply. Applicable at United States Club Champion stores only. Exclusions apply to free fittings; golf balls, grips, and OEM (Original Equipment Manufacturer) builds are not considered a custom equipment purchase. Waitlist appointments are not considered a booking and do not apply; the fitting must be booked and confirmed during the promotional period. Promotional fittings may be rescheduled no more than one time. Fitting must be completed in one appointment. Not valid on prior fittings or purchases. Not valid in conjunction with any other fitting offer or discount. Not valid in conjunction with PAR Program benefits. Club Champion reserves the right to remove or modify this promotion at any time. Appointments are limited.`;

export const MOCK_VALIDATED_PROMO_TERMS = `Promo code applied. Standard Club Champion promotional terms apply: discount valid for the booked fitting only; cannot be combined with other offers or PAR Program benefits; valid at United States Club Champion locations; rescheduling permitted no more than one time; fitting must be completed in a single appointment; not valid on prior bookings; subject to availability. Club Champion reserves the right to modify or revoke this promotion at any time.`;

// Australia's active promotion (CUSTOMFITAU, SF promo P-00805137). The bundled
// FITTINGUS copy in DEFAULT_PROMO_TERMS is US-specific — it names the wrong
// entity ("United States Club Champion stores only") and the wrong offer — so
// an AU store must never fall back to it. When /fittings returns the store's
// own activePromoTerms that value wins; this is the safety net for the window
// before the store fetch lands, and for the case where SF ships the code
// without a T&C string.
//
// NOTE: transcribed from the client's stated AU terms (item quoting P-00805137).
// The authoritative source is SF's Terms_and_Conditions__c surfaced via
// /fittings.activePromoTerms; confirm this bundled copy against that if the
// verbatim wording matters.
export const AU_PROMO_TERMS = `Get a Club Champion full bag fitting for $100 with a custom equipment purchase OR any other fitting type for $50. Promo excludes golf ball fittings. Promo code provided must be used at the time of booking. Club purchase must be made the same day as the club fitting for the discount to apply. Applicable at Club Champion Australia stores only. Exclusions apply; golf balls, grips, and OEM (Original Equipment Manufacturer) builds are not considered a custom equipment purchase. Waitlist appointments are not considered a booking and do not apply; the fitting must be booked and confirmed during the promotional period. Promotional fittings may be rescheduled no more than one time. Fitting must be completed in one appointment. Not valid on prior fittings or purchases. Not valid in conjunction with any other fitting offer or discount. Not valid in conjunction with PAR Program benefits. Club Champion reserves the right to remove or modify this promotion at any time. Appointments are limited.`;

/**
 * The bundled default terms for a market, used only as a fallback when the
 * store's own activePromoTerms has not (yet) been adopted.
 *
 * FITTINGUS is US copy and must not stand in for another market — it names
 * "United States Club Champion stores only", which misstates what an AU
 * customer is agreeing to. AU has its own promotion (CUSTOMFITAU), so it gets
 * its own default. Every other market keeps the US copy as the pre-store
 * placeholder it always was (the store's activePromoTerms still overrides it).
 */
export function defaultPromoTermsForCountry(country: string | null | undefined): string {
  if ((country ?? '').trim().toLowerCase() === 'au') return AU_PROMO_TERMS;
  return DEFAULT_PROMO_TERMS;
}

/**
 * Build the promoCode value sent on Schedule.
 *
 * The master promo is applied silently — it comes from state.masterPromoCode,
 * which is set by the engage effect (country default) then overwritten by
 * SF's activePromoCode on store select. It is never visible in the promo
 * input; users don't need to know the code to receive the discount.
 *
 * The user's own optional code (typically a referral like HALL8374) is
 * appended after `;` when it has been validated cleanly and is distinct
 * from the master. When empty or unvalidated, only the master is sent.
 * Falls back to DEFAULT_PROMO_CODE (CUSTOMFIT) if the master somehow
 * ends up blank.
 */
export function buildPromoCodeForSchedule(
  masterCode: string,
  userValidatedCode: string | null,
  validationValid: boolean | undefined,
): string {
  const master = masterCode.trim() || DEFAULT_PROMO_CODE;
  if (!userValidatedCode || !validationValid) return master;
  const user = userValidatedCode.trim();
  if (!user || user.toUpperCase() === master.toUpperCase()) return master;
  return `${master};${user}`;
}
